import translate from '@translate';
import React, { FC } from 'react';
import Grid, { GridItem } from 'src/components/Grid';
import TextField from 'src/components/TextField';
import { ProviderCompanyType } from 'src/types/providerCompany';
import Select from 'src/components/Select';
import getCountryOptions from 'src/utils/countryOptions';
import { BuildingIcon } from 'src/components/SkladonIcon/SmallIcons';
import { DeliveredIcon } from 'src/components/SkladonIcon/BigIcons';
import Typography from 'src/components/Typography';
import { styled } from '@mui/system';
import { TypographyProps } from '@mui/material/Typography';
import { sizes } from 'src/themes/default';
import { FormErrorsType, OnChangeType } from '../../types/global';
import { EditKeysType } from './index';

export interface DetailPropsType {
    onChange: OnChangeType<EditKeysType>;
    editData: ProviderCompanyType;
    editErrors: FormErrorsType;
    newProviderCompany: boolean;
    disableEdit: boolean;
}

const StyledIcon = styled('div')({
    display: 'inline-block',
    verticalAlign: 'top',

    '& svg': {
        width: '40px',
        height: '40px',
        marginRight: sizes.body_PD,
    },
});

const IconTextStyle: TypographyProps['sx'] = {
    display: 'inline-block',
    height: '40px',
    lineHeight: '40px',
    verticalAlign: 'middle',
};

const Detail: FC<DetailPropsType> = (props) => {
    return (
        <Grid>
            {/* Kontakt - levá část */}
            <GridItem cols={6}>
                <Grid>
                    <GridItem cols={12} sx={{ marginBottom: '14px' }}>
                        <StyledIcon>
                            <BuildingIcon />
                        </StyledIcon>
                        <Typography variant="h3" gutterBottom sx={IconTextStyle}>
                            {translate('providerCompanyDetail.contact')}
                        </Typography>
                    </GridItem>
                    <GridItem cols={6}>
                        <TextField
                            value={props.editData.RECIPIENT_NAME}
                            error={props.editErrors.RECIPIENT_NAME}
                            onChange={(value, error) => props.onChange('RECIPIENT_NAME', value, error)}
                            name="RECIPIENT_NAME"
                            label={translate('providerCompanyDetail.recipientName')}
                            fullWidth
                            required
                            multiline
                            disabled={props.disableEdit}
                            maxLength={35}
                        />
                    </GridItem>
                    <GridItem cols={6}>
                        <TextField
                            value={props.editData.CONTACT_PERSON}
                            error={props.editErrors.CONTACT_PERSON}
                            onChange={(value, error) => props.onChange('CONTACT_PERSON', value, error)}
                            name="CONTACT_PERSON"
                            label={translate('providerCompanyDetail.contactPerson')}
                            fullWidth
                            required
                            multiline
                            disabled={props.disableEdit}
                            maxLength={35}
                        />
                    </GridItem>
                    <GridItem cols={6}>
                        <TextField
                            value={props.editData.COMPANY_NAME}
                            error={props.editErrors.COMPANY_NAME}
                            onChange={(value, error) => props.onChange('COMPANY_NAME', value, error)}
                            name="COMPANY_NAME"
                            label={translate('providerCompanyDetail.companyName')}
                            fullWidth
                            multiline
                            disabled={props.disableEdit}
                            maxLength={35}
                        />
                    </GridItem>
                    <GridItem cols={6}>
                        <TextField
                            value={props.editData.GLN}
                            error={props.editErrors.GLN}
                            onChange={(value, error) => props.onChange('GLN', value, error)}
                            name="GLN"
                            label={translate('providerCompanyDetail.GLN')}
                            fullWidth
                            disabled={props.disableEdit}
                            maxLength={35}
                        />
                    </GridItem>
                    <GridItem cols={6}>
                        <TextField
                            value={props.editData.EMAIL}
                            error={props.editErrors.EMAIL}
                            onChange={(value, error) => props.onChange('EMAIL', value, error)}
                            name="EMAIL"
                            label={translate('providerCompanyDetail.email')}
                            fullWidth
                            required
                            multiline
                            disabled={props.disableEdit}
                            type="email"
                            maxLength={50}
                        />
                    </GridItem>
                    <GridItem cols={6}>
                        <TextField
                            value={props.editData.PHONE}
                            error={props.editErrors.PHONE}
                            onChange={(value, error) => props.onChange('PHONE', value, error)}
                            name="PHONE"
                            label={translate('providerCompanyDetail.phone')}
                            fullWidth
                            required
                            multiline
                            disabled={props.disableEdit}
                            maxLength={14}
                        />
                    </GridItem>
                    <GridItem cols={6}>
                        <TextField
                            value={props.editData.COMPANY_ID}
                            error={props.editErrors.COMPANY_ID}
                            onChange={(value, error) => props.onChange('COMPANY_ID', value, error)}
                            name="COMPANY_ID"
                            label={translate('providerCompanyDetail.companyId')}
                            fullWidth
                            multiline
                            disabled={props.disableEdit}
                            maxLength={14}
                        />
                    </GridItem>
                </Grid>
            </GridItem>
            {/* Adresa - pravá část */}
            <GridItem cols={6}>
                <Grid>
                    <GridItem cols={12} sx={{ marginBottom: '14px' }}>
                        <StyledIcon>
                            <DeliveredIcon />
                        </StyledIcon>
                        <Typography variant="h3" gutterBottom sx={IconTextStyle}>
                            {translate('providerCompanyDetail.address')}
                        </Typography>
                    </GridItem>
                    <GridItem cols={6}>
                        <TextField
                            value={props.editData.STREET}
                            error={props.editErrors.STREET}
                            onChange={(value, error) => props.onChange('STREET', value, error)}
                            name="STREET"
                            label={translate('providerCompanyDetail.street')}
                            fullWidth
                            required
                            multiline
                            disabled={props.disableEdit}
                            maxLength={35}
                        />
                    </GridItem>
                    <GridItem cols={6}>
                        <TextField
                            value={props.editData.CITY}
                            error={props.editErrors.CITY}
                            onChange={(value, error) => props.onChange('CITY', value, error)}
                            name="CITY"
                            label={translate('providerCompanyDetail.city')}
                            fullWidth
                            required
                            multiline
                            disabled={props.disableEdit}
                            maxLength={30}
                        />
                    </GridItem>
                    <GridItem cols={6}>
                        <TextField
                            value={props.editData.ZIP_CODE}
                            error={props.editErrors.ZIP_CODE}
                            onChange={(value, error) => props.onChange('ZIP_CODE', value, error)}
                            name="ZIP_CODE"
                            label={translate('providerCompanyDetail.zipCode')}
                            fullWidth
                            required
                            disabled={props.disableEdit}
                            maxLength={9}
                        />
                    </GridItem>
                    <GridItem cols={6}>
                        <Select
                            options={getCountryOptions()}
                            value={props.editData.STATE}
                            error={props.editErrors.STATE}
                            onChange={(value, error) => props.onChange('STATE', value, error)}
                            name="STATE"
                            label={translate('providerCompanyDetail.state')}
                            fullWidth
                            required
                            disabled={props.disableEdit}
                        />
                    </GridItem>
                    <GridItem cols={6}>
                        <TextField
                            value={props.editData.ADDRESS_ADDITIONAL_INFO}
                            error={props.editErrors.ADDRESS_ADDITIONAL_INFO}
                            onChange={(value, error) =>
                                props.onChange('ADDRESS_ADDITIONAL_INFO', value, error)
                            }
                            name="ADDRESS_ADDITIONAL_INFO"
                            label={translate('providerCompanyDetail.addressAdditionalInfo')}
                            fullWidth
                            multiline
                            disabled={props.disableEdit}
                            maxLength={35}
                        />
                    </GridItem>
                    <GridItem cols={6}>
                        <TextField
                            value={props.editData.PROVINCE_CODE}
                            error={props.editErrors.PROVINCE_CODE}
                            onChange={(value, error) => props.onChange('PROVINCE_CODE', value, error)}
                            name="PROVINCE_CODE"
                            label={translate('providerCompanyDetail.provinceCode')}
                            fullWidth
                            disabled={props.disableEdit}
                            maxLength={35}
                        />
                    </GridItem>
                </Grid>
            </GridItem>
        </Grid>
    );
};

export default Detail;
